// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { z } from 'zod';

const ReleaseInformationDtoSchema = z.object({
  emaiVersion: z.string(),
  releaseNotes: z.string(),
});

const ChangeListSchema = z
  .array(z.string())
  .default([])
  .transform(changes => changes.filter(change => change.trim().length > 0));

const ReleaseSchema = z.object({
  version: z.string().min(1),
  release_date: z.string().date(),
  change_log: z
    .object({
      new_features: ChangeListSchema,
      bug_fixes: ChangeListSchema,
      internal_improvements: ChangeListSchema,
    })
    .default({}),
});

export type Release = z.infer<typeof ReleaseSchema>;
export type ReleaseInformation = { emaiVersion: string; releases: Release[] };
export type ReleaseInformationErrorReason = 'loadError' | 'invalidData' | 'maintenance' | 'empty';
export type ReleaseChangeLogContent =
  | { status: 'ready'; releases: Release[] }
  | { status: 'loading' }
  | { status: 'error'; reason: ReleaseInformationErrorReason };

export function parseReleaseInformation(value: unknown): ReleaseInformation {
  const dto = ReleaseInformationDtoSchema.parse(value);
  const releases = z.array(ReleaseSchema).parse(JSON.parse(dto.releaseNotes.trim() ? dto.releaseNotes : '[]'));

  releases.sort(
    (a, b) =>
      b.release_date.localeCompare(a.release_date) || b.version.localeCompare(a.version, 'en', { numeric: true })
  );

  return { emaiVersion: dto.emaiVersion, releases };
}
